import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deliveryAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import { load } from '@cashfreepayments/cashfree-js';

function MyDeliveries() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentModal, setPaymentModal] = useState(null); // {orderId, amount, orderNumber}
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchDeliveries();
    const interval = setInterval(fetchDeliveries, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchDeliveries = async () => {
    try {
      const response = await deliveryAPI.getMyDeliveries();
      setDeliveries(response.data.data.orders);
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await deliveryAPI.updateStatus(orderId, status);
      alert('Status updated successfully!');
      fetchDeliveries();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  // New COD settlement payment function
  const initiateCodSettlement = async (orderId, amount, orderNumber) => {
    setProcessingPayment(true);

    try {
      console.log('💰 Initiating COD settlement payment for order:', orderNumber);

      // Create payment session
      const response = await deliveryAPI.createCodSettlementPayment(orderId);
      const { paymentSessionId } = response.data.data;

      console.log('✅ Payment session created:', paymentSessionId);

      // Initialize Cashfree SDK
      const cashfree = await load({
        mode: process.env.REACT_APP_CASHFREE_ENV === 'PROD' ? 'production' : 'sandbox'
      });

      console.log('✅ Cashfree SDK initialized');

      // Checkout options - will redirect to Cashfree payment page
      const checkoutOptions = {
        paymentSessionId: paymentSessionId,
        redirectTarget: '_self'
      };

      console.log('🚀 Redirecting to Cashfree checkout...');

      // Redirect to Cashfree checkout
      // After payment, Cashfree will redirect back to /delivery/my-deliveries?cod_settlement={orderId}
      cashfree.checkout(checkoutOptions);
    } catch (error) {
      console.error('❌ Failed to initiate COD settlement:', error);
      alert('Failed to initiate payment: ' + (error.response?.data?.message || error.message));
      setProcessingPayment(false);
      setPaymentModal(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      assigned_delivery: 'linear-gradient(135deg, #4ecdc4 0%, #44b8b1 100%)',
      picked_up: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
      on_the_way: 'linear-gradient(135deg, #ffa500 0%, #ff8c00 100%)',
      delivered: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)'
    };
    return colors[status] || 'linear-gradient(135deg, #666 0%, #888 100%)';
  };

  const getStatusIcon = (status) => {
    const icons = {
      assigned_delivery: '📦',
      picked_up: '🚴',
      on_the_way: '🛵',
      delivered: '✅'
    };
    return icons[status] || '📋';
  };

  if (loading) return <div className="loading">🔄 Loading your deliveries...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            🚗 My Deliveries
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Track and manage your deliveries • Auto-refreshes every 15 seconds
          </p>
        </div>

        {deliveries.length === 0 ? (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '120px', marginBottom: 'var(--space-lg)' }}>🚗</div>
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              color: 'var(--gray-700)',
              marginBottom: 'var(--space-md)'
            }}>
              No active deliveries
            </h2>
            <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
              Accept orders from Available Orders section to start delivering
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-xl)' }}>
            {deliveries.map(order => (
              <div key={order._id} className="card fade-in" style={{
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: 'var(--space-lg)',
                  right: 'var(--space-lg)',
                  padding: 'var(--space-sm) var(--space-lg)',
                  borderRadius: 'var(--radius-full)',
                  background: getStatusColor(order.status),
                  color: 'white',
                  fontWeight: '700',
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  boxShadow: 'var(--shadow-md)'
                }}>
                  <span>{getStatusIcon(order.status)}</span>
                  <span>{order.status.replace(/_/g, ' ')}</span>
                </div>

                {/* Order Header */}
                <div style={{ paddingRight: '180px', marginBottom: 'var(--space-lg)' }}>
                  <h2 style={{
                    fontSize: 'var(--font-size-3xl)',
                    fontWeight: '800',
                    color: 'var(--gray-900)',
                    marginBottom: 'var(--space-xs)'
                  }}>
                    Order #{order.orderNumber}
                  </h2>
                </div>

                {/* Pickup & Delivery Info */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'var(--space-lg)',
                  marginBottom: 'var(--space-lg)',
                  paddingBottom: 'var(--space-lg)',
                  borderBottom: '2px solid var(--gray-100)'
                }}>
                  {/* Pickup From */}
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-md)',
                      color: 'var(--gray-900)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-sm)'
                    }}>
                      <span>🏪</span>
                      <span>Pickup From</span>
                    </h3>
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(255, 107, 107, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid rgba(255, 107, 107, 0.2)'
                    }}>
                      <p style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: '700',
                        color: 'var(--primary)',
                        marginBottom: 'var(--space-sm)'
                      }}>
                        {order.restaurantId?.name || 'Restaurant'}
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-700)',
                        marginBottom: 'var(--space-xs)'
                      }}>
                        📍 {order.restaurantId?.address?.street || 'N/A'}, {order.restaurantId?.address?.city || 'N/A'}
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-700)'
                      }}>
                        📱 {order.restaurantId?.phone || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Deliver To */}
                  <div>
                    <h3 style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-md)',
                      color: 'var(--gray-900)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-sm)'
                    }}>
                      <span>🏠</span>
                      <span>Deliver To</span>
                    </h3>
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(78, 205, 196, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid rgba(78, 205, 196, 0.2)'
                    }}>
                      <p style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: '700',
                        color: 'var(--secondary)',
                        marginBottom: 'var(--space-sm)'
                      }}>
                        {order.userId?.name || order.userInfo?.name || 'Customer'}
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-700)',
                        marginBottom: 'var(--space-xs)'
                      }}>
                        📍 {order.deliveryAddress?.street || 'N/A'}, {order.deliveryAddress?.city || 'N/A'}
                      </p>
                      <p style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-700)'
                      }}>
                        📱 {order.userId?.phone || order.userInfo?.phone || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Order Details */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 'var(--space-md)',
                  marginBottom: 'var(--space-xl)'
                }}>
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(255, 184, 77, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(255, 184, 77, 0.2)',
                    textAlign: 'center'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      ORDER TOTAL
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      color: 'var(--warning)',
                      margin: 0
                    }}>
                      ₹{order.pricing?.total || 0}
                    </p>
                  </div>

                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(78, 205, 196, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(78, 205, 196, 0.2)',
                    textAlign: 'center'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      PAYMENT
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: '700',
                      color: 'var(--secondary)',
                      margin: 0,
                      textTransform: 'uppercase'
                    }}>
                      💳 {order.paymentMethod || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  {order.status === 'assigned_delivery' && (
                    <button
                      onClick={() => updateStatus(order._id, 'picked_up')}
                      className="btn btn-primary btn-lg"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-sm)'
                      }}
                    >
                      <span>📦</span>
                      <span>Mark as Picked Up</span>
                    </button>
                  )}

                  {order.status === 'picked_up' && (
                    <button
                      onClick={() => updateStatus(order._id, 'on_the_way')}
                      className="btn btn-primary btn-lg"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-sm)'
                      }}
                    >
                      <span>🛵</span>
                      <span>Mark On the Way</span>
                    </button>
                  )}

                  {order.status === 'on_the_way' && (
                    <button
                      onClick={() => updateStatus(order._id, 'delivered')}
                      className="btn btn-success btn-lg"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-sm)'
                      }}
                    >
                      <span>✅</span>
                      <span>Mark as Delivered</span>
                    </button>
                  )}

                  {/* COD Settlement Payment Button - Updated */}
                  {order.status === 'delivered' && order.paymentMethod === 'cash' && !order.codSettled && (
                    <button
                      onClick={() => setPaymentModal({ orderId: order._id, amount: order.pricing?.total || 0, orderNumber: order.orderNumber })}
                      className="btn btn-warning btn-lg"
                      disabled={processingPayment}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--space-sm)',
                        background: 'linear-gradient(135deg, #ff9800 0%, #ff6b00 100%)',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: 'var(--font-size-lg)',
                        padding: 'var(--space-lg)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        cursor: processingPayment ? 'not-allowed' : 'pointer',
                        opacity: processingPayment ? 0.7 : 1
                      }}
                    >
                      <span>💰</span>
                      <span>{processingPayment ? 'Processing...' : `Settle COD Payment (₹${order.pricing?.total || 0})`}</span>
                    </button>
                  )}

                  {order.status === 'delivered' && (order.paymentMethod !== 'cash' || order.codSettled) && (
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(81, 207, 102, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid rgba(81, 207, 102, 0.2)',
                      textAlign: 'center'
                    }}>
                      <p style={{
                        margin: 0,
                        color: 'var(--success)',
                        fontWeight: '700',
                        fontSize: 'var(--font-size-lg)'
                      }}>
                        ✅ {order.paymentMethod === 'cash' ? 'Delivery & Payment Settled' : 'Delivery Completed Successfully'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Hover Accent */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '200px',
                  height: '200px',
                  background: 'radial-gradient(circle at bottom left, rgba(78, 205, 196, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* COD Settlement Payment Modal */}
      {paymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 'var(--space-xl)'
        }}>
          <div className="card" style={{
            maxWidth: '500px',
            width: '100%',
            padding: 'var(--space-2xl)',
            position: 'relative'
          }}>
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '800',
              marginBottom: 'var(--space-lg)',
              textAlign: 'center',
              background: 'var(--gradient-primary)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              💰 COD Settlement Payment
            </h2>

            <div style={{
              background: 'rgba(255, 184, 77, 0.1)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-lg)',
              marginBottom: 'var(--space-xl)',
              border: '1px solid rgba(255, 184, 77, 0.2)'
            }}>
              <p style={{ marginBottom: 'var(--space-md)', color: 'var(--gray-700)' }}>
                <strong>Order:</strong> #{paymentModal.orderNumber}
              </p>
              <p style={{ marginBottom: 'var(--space-md)', fontSize: 'var(--font-size-2xl)', fontWeight: '800', color: 'var(--warning)' }}>
                Amount: ₹{paymentModal.amount}
              </p>
              <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
                You collected ₹{paymentModal.amount} in cash from the customer. Transfer this amount to Foodwinc's account through our secure payment gateway.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
              <button
                onClick={() => setPaymentModal(null)}
                disabled={processingPayment}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={() => initiateCodSettlement(paymentModal.orderId, paymentModal.amount, paymentModal.orderNumber)}
                disabled={processingPayment}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                {processingPayment ? '⏳ Processing...' : '💳 Pay Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyDeliveries;
